import { useEffect, useMemo, useState } from 'react';
import { type SortingState } from '@tanstack/react-table';
import Input from '../common/Input';
import Table from '../common/Table';
import { createBounceEmailColumns } from './dashboardColumns';
import { BounceEmailRecord } from '../types';
import { useBounceEmailLogsQuery } from '../hooks/useBounceEmailLogs';

function BounceEmailsTab() {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [sorting, setSorting] = useState<SortingState>([{ id: 'bouncedAt', desc: true }]);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const activeSort = sorting[0];
  const sortField = activeSort?.id ? String(activeSort.id) : '';
  const sortDirection: 'asc' | 'desc' = activeSort?.desc ? 'desc' : 'asc';
  const bounceEmailLogsQuery = useBounceEmailLogsQuery(true, pageIndex + 1, pageSize, debouncedSearchTerm, sortField, sortDirection);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setPageIndex(0);
  }, [debouncedSearchTerm, sortField, sortDirection]);

  const columns = useMemo(() => createBounceEmailColumns(), []);
  const bounceEmailLogs = useMemo<BounceEmailRecord[]>(() => bounceEmailLogsQuery.data?.data ?? [], [bounceEmailLogsQuery.data?.data]);
  const bounceEmailMeta = bounceEmailLogsQuery.data?.meta;
  const bounceEmailsErrorMessage = (bounceEmailLogsQuery.error as Error | null)?.message ?? '';

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <Table<BounceEmailRecord>
        data={bounceEmailLogs}
        columns={columns}
        loading={bounceEmailLogsQuery.isFetching}
        enableSorting
        sorting={sorting}
        onSortingChange={setSorting}
        pagination={{
          pageIndex,
          pageSize,
          totalCount: bounceEmailMeta?.total ?? bounceEmailLogs.length,
          totalPages: bounceEmailMeta?.totalPages,
          onPageChange: setPageIndex,
          onPageSizeChange: setPageSize,
          pageSizeOptions: [10, 20, 40, 80],
        }}
        topHeader={
          <div className="max-w-lg">
            <Input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search by email, subject, action, bounce type, code, or reason"
              aria-label="Search bounce emails"
            />
          </div>
        }
      />
      {bounceEmailsErrorMessage && (
        <p className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{bounceEmailsErrorMessage}</p>
      )}
    </div>
  );
}

export default BounceEmailsTab;
